import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db, auth } from '../services/firebase';
import { 
  collection,
  doc, 
  setDoc, 
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { useAuthContext } from './AuthContext';
import { formatDate } from '../utils/dateFormatter';
import BrokerActionPopup from '../components/notifications/BrokerActionPopup';
import brokerNotificationService from '../services/brokerNotificationService';
// automationLogService removed - no longer storing automation logs

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, userProfile } = useAuthContext();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // รวม automation logs เข้ากับ notifications
  const [allNotifications, setAllNotifications] = useState([]);

  const [defaultNotifications, setDefaultNotifications] = useState([]);
  const [automationNotifications, setAutomationNotifications] = useState([]);
  // const [automationLogs, setAutomationLogs] = useState([]); // Removed - no longer storing automation logs
  // const [automationStats, setAutomationStats] = useState(null); // Removed - no longer storing automation logs
  const [editNotifications, setEditNotifications] = useState([]);
  const [brokerLoginNotifications, setBrokerLoginNotifications] = useState([]);
  const [engagementNotifications, setEngagementNotifications] = useState([]);
  const [robotOrderNotifications, setRobotOrderNotifications] = useState([]);
  const [stripePaymentNotifications, setStripePaymentNotifications] = useState([]);
  const [tradeHistoryNotifications, setTradeHistoryNotifications] = useState([]);
  const [orderStatusNotifications, setOrderStatusNotifications] = useState([]); // NEW: Order status tracking
  const [billingData, setBillingData] = useState({
    subscription: null,
    paymentHistory: [],
    upcomingPayments: []
  });

  // Broker action popup state
  const [showBrokerActionPopup, setShowBrokerActionPopup] = useState(false);
  const [selectedBrokerAction, setSelectedBrokerAction] = useState(null);


  useEffect(() => {

    if (!user || !userProfile) {
      setDefaultNotifications([]);
      setAutomationNotifications([]);
              // setAutomationLogs([]); // Removed - no longer storing automation logs
      // setAutomationStats(null); // Removed - no longer storing automation logs
        setEditNotifications([]);
        setBrokerLoginNotifications([]);
        // setEngagementNotifications([]); // ปิดการใช้งาน tbl_engagement
        setRobotOrderNotifications([]);
        setStripePaymentNotifications([]);
        setTradeHistoryNotifications([]);
        setOrderStatusNotifications([]); // NEW: Clear order status notifications
      setLoading(false);
      return;
    }

    const userId = user.uid;

    // Listener for tbl_notifications collection
    const notificationsQuery = query(
      collection(db, 'tbl_notifications'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    ); 

    const unsubscribeNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setDefaultNotifications([]);
        } else {
          const notificationsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
              formattedDate: formatDate(data.timestamp?.toDate() || new Date()),
              type: 'notification'
            };
          });
          setDefaultNotifications(notificationsData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to tbl_notifications:', err);
        setError(err);
        setLoading(false);
      }
    );

    // Listener for tbl_automation_logs collection - removed
    // No longer storing automation logs

    // Listener for Edit_status collection  
    const editStatusQuery = query(
      collection(db, 'Edit_status'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeEditStatus = onSnapshot(
      editStatusQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setEditNotifications([]);
        } else {
          const editData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
              formattedDate: formatDate(data.timestamp?.toDate() || new Date()),
              type: 'edit'
            };
          });
          setEditNotifications(editData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to Edit_status:', err);
      }
    );



    // Listener for tbl_engagement collection - ปิดการใช้งาน
    /*
    const engagementQuery = query(
      collection(db, 'tbl_engagement'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeEngagement = onSnapshot(
      engagementQuery,
      (snapshot) => {
        console.log('🔔 NotificationContext: Received snapshot for tbl_engagement');
        console.log('🔔 NotificationContext: Engagement size:', snapshot.size);
        
        if (snapshot.empty) {
          console.log('🔔 NotificationContext: No engagement data found');
          setEngagementNotifications([]);
        } else {
          const engagementData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
              formattedDate: formatDate(data.timestamp?.toDate() || new Date()),
              type: 'engagement'
            };
          });
          
          console.log('🔔 NotificationContext: Fetched engagement data:', engagementData);
          setEngagementNotifications(engagementData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to tbl_engagement:', err);
      }
    );
    */

    // Listener for tbl_robot_orders collection
    const robotOrdersQuery = query(
      collection(db, 'tbl_robot_orders'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeRobotOrders = onSnapshot(
      robotOrdersQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setRobotOrderNotifications([]);
        } else {
          const robotData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
              formattedDate: formatDate(data.timestamp?.toDate() || new Date()),
              type: 'robot'
            };
          });
          setRobotOrderNotifications(robotData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to tbl_robot_orders:', err);
      }
    );

    // Listener for subscription data
    const subscriptionQuery = query(
      collection(db, 'tbl_subscriptions'),
      where('userId', '==', userId),
      limit(1)
    );

    const unsubscribeSubscription = onSnapshot(
      subscriptionQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setBillingData(prev => ({ ...prev, subscription: null }));
        } else {
          const subscriptionData = snapshot.docs[0].data();
          setBillingData(prev => ({
            ...prev,
            subscription: subscriptionData
          }));
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to tbl_subscriptions:', err);
      }
    );

    // Listener for tbl_trade_history collection
    const tradeHistoryQuery = query(
      collection(db, 'tbl_trade_history'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeTradeHistory = onSnapshot(
      tradeHistoryQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setTradeHistoryNotifications([]);
        } else {
          const tradeData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
              type: 'trade'
            };
          });
          setTradeHistoryNotifications(tradeData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to tbl_trade_history:', err);
      }
    );

    // NEW: Listener for tbl_user_order_logs collection - Real-time order status tracking
    // This shows pending/filled/exited status in notifications
    // tbl_user_order_logs is written by the TS backend in snake_case
    // (user_id, order_time, fill_status, ...) — unlike the camelCase +
    // 'timestamp' collections above. Filtering on 'userId' / ordering by
    // 'timestamp' matched zero docs, so order-fill notifications never fired.
    // Filter on user_id and sort client-side below.
    // ponytail: no (user_id, order_time) composite index exists and we don't
    // create infra here; this sorts the fetched window. If a user exceeds ~100
    // logs and misses recent ones, add the index and restore a server orderBy.
    const orderLogsQuery = query(
      collection(db, 'tbl_user_order_logs'),
      where('user_id', '==', userId),
      limit(100) // More orders for better tracking
    );

    const unsubscribeOrderLogs = onSnapshot(
      orderLogsQuery,
      (snapshot) => {
        console.log('📊 NotificationContext: Order logs snapshot received', {
          size: snapshot.size,
          empty: snapshot.empty
        });

        if (snapshot.empty) {
          setOrderStatusNotifications([]);
        } else {
          const orderData = snapshot.docs.map(doc => {
            const data = doc.data();

            // Map order status to user-friendly messages
            const getOrderStatusMessage = (status, fillStatus) => {
              const statusMap = {
                'pending': 'pending',
                'working': 'pending',
                'submitted': 'pending',
                'accepted': 'pending',
                'open': 'pending',
                'filled': 'filled',
                'complete': 'filled',
                'partial': 'partially filled',
                'partially_filled': 'partially filled',
                'cancelled': 'cancelled',
                'canceled': 'cancelled',
                'rejected': 'rejected',
                'expired': 'expired',
                'closed': 'exited' // This is the key status for closed positions
              };

              return statusMap[status?.toLowerCase()] || status || 'unknown';
            };

            const displayStatus = getOrderStatusMessage(data.status, data.fill_status);

            // Create notification message
            const symbol = data.symbol || 'Unknown';
            const side = data.side || '';
            const quantity = data.quantity || '';

            let message = `Order ${displayStatus}: ${symbol}`;
            if (side && quantity) {
              message = `${side} ${quantity}x ${symbol} - ${displayStatus}`;
            }

            // Add average fill price if available
            if (data.average_fill_price && displayStatus === 'filled') {
              message += ` @ $${data.average_fill_price.toFixed(2)}`;
            }

            return {
              id: doc.id,
              ...data,
              timestamp: data.order_time?.toDate() || data.last_update_time?.toDate() || new Date(),
              formattedDate: formatDate(data.order_time?.toDate() || data.last_update_time?.toDate() || new Date()),
              type: 'order_status',
              message,
              displayStatus, // pending, filled, exited
              groupId: data.group_id,
              broker: data.broker,
              accountId: data.account_id
            };
          });

          // Sort newest-first client-side (no server orderBy — see query above)
          orderData.sort((a, b) => b.timestamp - a.timestamp);

          console.log('📊 NotificationContext: Processed order status notifications:', {
            count: orderData.length,
            statuses: orderData.map(o => ({ id: o.id, status: o.displayStatus, symbol: o.symbol }))
          });

          setOrderStatusNotifications(orderData);
        }
      },
      (err) => {
        console.error('❌ NotificationContext: Error listening to tbl_user_order_logs:', err);
      }
    );

    // Listener for tbl_broker_notifications collection (ใหม่)
    const brokerNotificationsQuery = query(
      collection(db, 'tbl_broker_notifications'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeBrokerNotifications = onSnapshot(
      brokerNotificationsQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setBrokerLoginNotifications([]);
        } else {
          const brokerNotificationsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(data.createdAt),
              type: 'broker_login',
              log: data.log || `${data.broker} - ${data.message}`,
              broker: data.broker,
              accountId: data.accountId,
              accountName: data.accountName,
              sessionId: data.sessionId,
              status: data.status
            };
          });
          setBrokerLoginNotifications(brokerNotificationsData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to tbl_broker_notifications:', err);
      }
    );

    // Listener for broker disconnection notifications from tbl_broker_integration
    const brokerDisconnectionQuery = query(
      collection(db, 'tbl_broker_integration'),
      where('userId', '==', userId),
      where('type', '==', 'disconnection'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeBrokerDisconnections = onSnapshot(
      brokerDisconnectionQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const disconnectionData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(data.createdAt),
              type: 'broker_disconnection',
              log: data.log || `${data.broker} - ${data.message}`,
              broker: data.broker,
              accountId: data.accountId,
              accountName: data.accountName,
              status: data.status
            };
          });
          // Replace instead of append to prevent duplicates
          setBrokerLoginNotifications(disconnectionData);
        }
      },
      (err) => {
        console.error('🔔 NotificationContext: Error listening to broker disconnections:', err);
      }
    );

    // Cleanup function
    return () => {
      unsubscribeNotifications();
      unsubscribeEditStatus();
      unsubscribeBrokerNotifications();
      unsubscribeBrokerDisconnections();
      unsubscribeRobotOrders();
      unsubscribeSubscription();
      unsubscribeTradeHistory();
      unsubscribeOrderLogs(); // NEW: Cleanup order logs listener
    };
  }, [user, userProfile]);

  // Effect to combine notifications with optimization to prevent excessive re-renders
  useEffect(() => {
    // Use JSON.stringify to check if the actual data has changed, not just references
    const currentIds = [
      ...defaultNotifications.map(n => n.id),
      ...robotOrderNotifications.map(n => n.id),
      ...stripePaymentNotifications.map(n => n.id),
      ...tradeHistoryNotifications.map(n => n.id),
      ...orderStatusNotifications.map(n => n.id) // NEW: Include order status notifications
    ].sort();

    const previousIds = notifications.map(n => n.id).sort();

    // Only update if the IDs actually changed
    if (JSON.stringify(currentIds) === JSON.stringify(previousIds)) {
      console.log('📊 NotificationContext: No notification changes detected, skipping combine');
      return;
    }

    console.log('🔄 NotificationContext: Combining notifications...');
    const combined = [
      ...defaultNotifications,
      ...robotOrderNotifications,
      ...stripePaymentNotifications,
      ...tradeHistoryNotifications,
      ...orderStatusNotifications // NEW: Include order status notifications
    ].sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0));

    setNotifications(combined);
    setAllNotifications(combined);
    setUnreadCount(combined.filter(n => !n.read).length);
  }, [defaultNotifications, robotOrderNotifications, stripePaymentNotifications, tradeHistoryNotifications, orderStatusNotifications]);

  // Add notification with specific username (fallback function)
  const addNotificationWithUsername = async (message, type = 'info', metadata = {}, username) => {
    console.log('📢 addNotificationWithUsername called:', {
      message,
      type,
      username,
      user: user?.uid
    });
    
    if (!user) {
      console.error('❌ User not authenticated');
      throw new Error('User must be authenticated');
    }

    setError(null);
    try {
      const notificationId = `${username}_${Date.now()}`;
      
      const notification = {
        username: username,
        userId: user.uid,
        message: message,
        type: type,
        timestamp: serverTimestamp(),
        isRead: false,
        metadata: {
          ...metadata,
          userEmail: user.email || 'unknown@email.com',
          source: metadata.source || 'system'
        }
      };

      console.log('📝 Creating notification with fallback username:', notification);
      await setDoc(doc(db, 'tbl_notifications', notificationId), notification);
      console.log('✅ Notification saved to Firestore:', notificationId);
      
      return notificationId;
    } catch (error) {
      console.error('❌ Failed to send notification:', error);
      setError(error);
      throw error;
    }
  };

  // Add new notification
  const addNotification = async (message, type = 'info', metadata = {}) => {
    console.log('📢 addNotification called:', {
      message,
      type,
      user: user?.uid,
      userProfile: userProfile?.username
    });
    
    console.log('🔍 Debug - Full user object:', user);
    console.log('🔍 Debug - Full userProfile object:', userProfile);
    
    // If user is not ready, skip notification to avoid blocking login
    if (!user) {
      console.warn('⏳ User not authenticated yet. Skipping notification to avoid blocking login flow.');
      return null;
    }

    // If userProfile is not available, use fallback immediately
    if (!userProfile?.username) {
      if (user?.email) {
        const fallbackUsername = user.email.split('@')[0];
        return await addNotificationWithUsername(message, type, metadata, fallbackUsername);
      }
      const rememberedEmail = localStorage.getItem('rememberedEmail');
      if (rememberedEmail) {
        const fallbackUsername = rememberedEmail.split('@')[0];
        return await addNotificationWithUsername(message, type, metadata, fallbackUsername);
      }
      const defaultUsername = 'user_' + Date.now();
      return await addNotificationWithUsername(message, type, metadata, defaultUsername);
    }

    setError(null);
    try {
      // Using a combination of username and timestamp for a unique ID (could be improved)
      const notificationId = `${userProfile.username}_${Date.now()}`;
      
      const notification = {
        username: userProfile.username,
        userId: user.uid, // Add userId
        message: message,
        type: type, // info, success, warning, error, trade, broker
        timestamp: serverTimestamp(), // Use server timestamp
        isRead: false,
        metadata: {
          ...metadata,
          userEmail: user.email || userProfile.email || 'unknown@email.com',
          source: metadata.source || 'system' // Default source
        }
      };

      console.log('📝 Creating notification:', notification);

      // Write to the tbl_notifications collection
      await setDoc(doc(db, 'tbl_notifications', notificationId), notification);
      console.log('✅ Notification saved to Firestore:', notificationId);
      
      return notificationId;
    } catch (error) {
      console.error('❌ Failed to send notification:', error);
      setError(error);
      throw error;
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    if (!user) return;

    try {
      const notificationRef = doc(db, 'tbl_notifications', notificationId);
      await updateDoc(notificationRef, {
        isRead: true,
        readAt: serverTimestamp()
      });

      // Update local state immediately for better responsiveness
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, isRead: true }
            : notif
        )
      );
      
      // Recalculate unread count (optional, can be derived)
      // setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user || notifications.length === 0) return;

    try {
      // Filter for unread notifications
      const unreadNotifications = notifications.filter(n => !n.isRead);
      
      // Update all unread notifications in Firestore
      const updatePromises = unreadNotifications.map(notif =>
        updateDoc(doc(db, 'tbl_notifications', notif.id), {
          isRead: true,
          readAt: serverTimestamp()
        })
      );

      await Promise.all(updatePromises);

      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      
      // setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    if (!user) return;

    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'tbl_notifications', notificationId));

      // Update local state
      setNotifications(prev =>
        prev.filter(notif => notif.id !== notificationId)
      );
      
      // Update unread count if the deleted one was unread (optional)
      // const deletedNotif = notifications.find(n => n.id === notificationId);
      // if (deletedNotif && !deletedNotif.isRead) {
      //   setUnreadCount(prev => Math.max(0, prev - 1));
      // }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    if (!user || notifications.length === 0) return;

    try {
      // Delete all notifications from Firestore
      // Note: For a large number of notifications, this should be done in batches
      const deletePromises = notifications.map(notif =>
        deleteDoc(doc(db, 'tbl_notifications', notif.id))
      );

      await Promise.all(deletePromises);

      // Clear local state
      setNotifications([]);
      // setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  // Recalculate unread count whenever notifications change
  useEffect(() => {
    const unread = notifications.filter(notif => !notif.isRead).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Broker action functions
  const showBrokerActionWarning = useCallback((broker, action) => {
    if (broker.status === 'pending' || broker.status === 'filled') {
      setSelectedBrokerAction({ broker, action });
      setShowBrokerActionPopup(true);
      return true; // Show warning
    }
    return false; // No warning needed
  }, []);

  const handleBrokerActionProceed = useCallback(() => {
    if (selectedBrokerAction) {
      // Execute the action
      console.log('⚠️ Proceeding with broker action despite open positions:', selectedBrokerAction);
      // Add your broker action logic here
    }
    setShowBrokerActionPopup(false);
    setSelectedBrokerAction(null);
  }, [selectedBrokerAction]);

  const handleBrokerActionCancel = useCallback(() => {
    setShowBrokerActionPopup(false);
    setSelectedBrokerAction(null);
  }, []);

  // Broker notification helper functions (ใหม่)
  const createBrokerLoginNotification = useCallback(async (broker, accountInfo) => {
    if (!user) {
      console.warn('⚠️ User not authenticated, skipping broker login notification');
      return null;
    }

    try {
      const result = await brokerNotificationService.createLoginNotification(
        user.uid,
        broker,
        accountInfo
      );

      if (result.success) {
        console.log('✅ Broker login notification created successfully');
        return result.notification;
      } else {
        console.error('❌ Failed to create broker login notification:', result.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Error creating broker login notification:', error);
      return null;
    }
  }, [user]);

  const updateBrokerLogoutNotification = useCallback(async (sessionId) => {
    if (!user) {
      console.warn('⚠️ User not authenticated, skipping broker logout notification');
      return false;
    }

    try {
      const result = await brokerNotificationService.updateLogoutNotification(
        sessionId,
        new Date()
      );

      if (result.success) {
        console.log('✅ Broker logout notification updated successfully');
        return true;
      } else {
        console.error('❌ Failed to update broker logout notification:', result.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Error updating broker logout notification:', error);
      return false;
    }
  }, [user]);

  const createBrokerErrorNotification = useCallback(async (broker, errorType, errorMessage, accountInfo = {}) => {
    if (!user) {
      console.warn('⚠️ User not authenticated, skipping broker error notification');
      return null;
    }

    try {
      const result = await brokerNotificationService.createErrorNotification(
        user.uid,
        broker,
        errorType,
        errorMessage,
        accountInfo
      );

      if (result.success) {
        console.log('✅ Broker error notification created successfully');
        return result.notification;
      } else {
        console.error('❌ Failed to create broker error notification:', result.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Error creating broker error notification:', error);
      return null;
    }
  }, [user]);

  // Create notification helpers for common events
  const notificationHelpers = {
    // Trade notifications
    tradeExecuted: (details) => addNotification(
      `Trade executed: ${details.symbol} ${details.action} ${details.quantity} @ $${details.price}`,
      'trade',
      { tradeId: details.tradeId, broker: details.broker }
    ),
    
    orderFilled: (details) => addNotification(
      `Order filled: ${details.symbol} ${details.side} ${details.quantity} shares`,
      'success',
      { orderId: details.orderId, broker: details.broker }
    ),
    
    orderCancelled: (details) => addNotification(
      `Order cancelled: ${details.symbol}`,
      'warning',
      { orderId: details.orderId }
    ),

    // Broker notifications (ใหม่ - ใช้ brokerNotificationService)
    brokerConnected: async (brokerName, accountInfo) => {
      if (!user) return null;
      
      try {
        const result = await brokerNotificationService.createLoginNotification(
          user.uid,
          brokerName,
          accountInfo
        );
        
        if (result.success) {
          console.log('✅ Broker login notification created:', result.notification);
          // Also add a simple feed entry for the notifications panel
          try {
            await addNotification(
              `${brokerName} connection established`,
              'broker',
              { source: 'broker', broker: brokerName, action: 'connected' }
            );
          } catch (e) {
            console.warn('⚠️ Failed to add simple broker connected feed entry:', e);
          }
          return result.notification.id;
        } else {
          console.error('❌ Failed to create broker login notification:', result.error);
          return null;
        }
      } catch (error) {
        console.error('❌ Error creating broker login notification:', error);
        return null;
      }
    },
    
    brokerDisconnected: async (brokerName, sessionId) => {
      if (!user) return null;
      
      try {
        const result = await brokerNotificationService.updateLogoutNotification(
          sessionId,
          new Date()
        );
        
        if (result.success) {
          console.log('✅ Broker logout notification updated');
          // Also add a simple feed entry for the notifications panel
          try {
            await addNotification(
              `${brokerName} connection removed`,
              'broker',
              { source: 'broker', broker: brokerName, action: 'disconnected' }
            );
          } catch (e) {
            console.warn('⚠️ Failed to add simple broker disconnected feed entry:', e);
          }
          return true;
        } else {
          console.error('❌ Failed to update broker logout notification:', result.error);
          return false;
        }
      } catch (error) {
        console.error('❌ Error updating broker logout notification:', error);
        return false;
      }
    },
    
    brokerError: async (brokerName, errorType, errorMessage, accountInfo = {}) => {
      if (!user) return null;
      
      try {
        const result = await brokerNotificationService.createErrorNotification(
          user.uid,
          brokerName,
          errorType,
          errorMessage,
          accountInfo
        );
        
        if (result.success) {
          console.log('✅ Broker error notification created:', result.notification);
          return result.notification.id;
        } else {
          console.error('❌ Failed to create broker error notification:', result.error);
          return null;
        }
      } catch (error) {
        console.error('❌ Error creating broker error notification:', error);
        return null;
      }
    },

    // Account notifications
    loginSuccess: async () => {
      console.log('🔔 loginSuccess helper called');
      try {
        const result = await addNotification(
          'Successfully logged in',
          'success',
          { source: 'auth' }
        );
        console.log('✅ loginSuccess notification added:', result);
        return result;
      } catch (error) {
        console.error('❌ loginSuccess notification failed:', error);
        throw error;
      }
    },
    
    profileUpdated: () => addNotification(
      'Profile updated successfully',
      'success',
      { source: 'profile' }
    ),
    
    subscriptionChanged: (plan) => addNotification(
      `Subscription changed to ${plan} plan`,
      'info',
      { source: 'subscription' }
    ),

    // System notifications
    systemMessage: (message) => addNotification(
      message,
      'info',
      { source: 'system' }
    ),
    
    maintenanceNotice: (message) => addNotification(
      `Maintenance Notice: ${message}`,
      'warning',
      { source: 'system' }
    ),

    // Automation log helpers - removed
    // No longer storing automation logs
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    notify: notificationHelpers,
    // Billing data
    billingData,
    // Broker action popup
    showBrokerActionPopup,
    selectedBrokerAction,
    showBrokerActionWarning,
    handleBrokerActionProceed,
    handleBrokerActionCancel,
    // Broker notification helpers (ใหม่)
    createBrokerLoginNotification,
    updateBrokerLogoutNotification,
    createBrokerErrorNotification,
    // Automation logs - removed
    allNotifications, // รวม notifications ทั้งหมด
    // Broker integration notifications for NotificationDropdown
    brokerIntegrationNotifications: brokerLoginNotifications,
    // Order status notifications
    orderStatusNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Broker Action Popup */}
      <BrokerActionPopup
        isOpen={showBrokerActionPopup}
        onClose={handleBrokerActionCancel}
        onProceed={handleBrokerActionProceed}
        onCancel={handleBrokerActionCancel}
        brokerName={selectedBrokerAction?.broker?.broker}
      />
    </NotificationContext.Provider>
  );
}; 